import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { loadLabelFormat, resolveFormat, FIELD_OPTS } from "@/lib/labelFormat";

/**
 * Compact chip that shows the currently-saved label format at a glance and
 * links to the Custom Labels page for editing. Used next to every label
 * "Print" button so operators always know what will come out of the printer.
 *
 * Re-reads from localStorage on window focus + storage events so it stays in
 * sync if the format is changed in another tab.
 */
export default function LabelFormatBadge({ dark = false, className = "" }) {
  const [fmt, setFmt] = useState(loadLabelFormat);

  useEffect(() => {
    const refresh = () => setFmt(loadLabelFormat());
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const { preset, family, scale, fieldSet } = resolveFormat(fmt);
  const fieldCount = FIELD_OPTS.filter((f) => fieldSet.has(f.key)).length;

  // Thumbnail height fixed at 22px; width scaled to preset aspect ratio (capped
  // so ultra-tall packing sheets don't disappear to a sliver).
  const H = 22;
  const rawW = (preset.pageW / preset.pageH) * H;
  const W = Math.min(46, Math.max(14, rawW));

  const border = dark ? "border-zinc-700" : "border-zinc-300";
  const bg = dark ? "bg-zinc-900" : "bg-white";
  const text = dark ? "text-zinc-300" : "text-zinc-700";
  const muted = dark ? "text-zinc-500" : "text-zinc-500";
  const hover = dark ? "hover:bg-zinc-800" : "hover:bg-orange-50";
  const editText = dark ? "text-orange-400" : "text-orange-700";

  return (
    <Link
      to="/labels/custom"
      target="_blank"
      rel="noreferrer"
      data-testid="label-format-badge"
      title={`Label format: ${preset.label} · ${family.label} · ${scale.label} · ${fieldCount} field${fieldCount === 1 ? "" : "s"} · click to edit`}
      className={`inline-flex items-center gap-2 border ${border} ${bg} ${hover} px-2 py-1 text-[10px] font-mono ${text} transition-colors ${className}`}
    >
      {/* Aspect-ratio thumbnail */}
      <span
        className={`inline-flex items-center justify-center border ${dark ? "border-zinc-600 bg-zinc-950" : "border-zinc-950 bg-white"} shrink-0`}
        style={{ width: `${W}px`, height: `${H}px`, fontFamily: family.css, boxSizing: "border-box" }}
      >
        <span
          className={`${dark ? "text-zinc-200" : "text-zinc-950"} font-extrabold leading-none`}
          style={{ fontSize: `${Math.max(6, 8 * scale.mult)}px`, letterSpacing: "-0.03em" }}
        >
          WPL
        </span>
      </span>

      <span className="flex flex-col leading-tight">
        <span className="uppercase tracking-wider">
          {preset.pageW}×{preset.pageH}
          {preset.perPage > 1 ? ` · ${preset.perPage}-up` : ""}
        </span>
        <span className={`${muted} text-[9px]`}>
          {family.label.split(" ")[0]} · {scale.key.toUpperCase()} · {fieldCount} fields
        </span>
      </span>

      <span className={`inline-flex items-center gap-1 pl-1 border-l ${border} ${editText}`}>
        <Pencil size={10} />
        <span className="uppercase tracking-wider">Edit</span>
      </span>
    </Link>
  );
}
